/**
 * Programs — public list page for symposiums (or stages, depending on `kind` prop).
 * Displays cards with cover, dates, title, subtitle, and a "Découvrir" link.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/i18n/I18nContext";
import { pickLocalized } from "@/i18n/translations";
import Ornament from "@/components/Ornament";

const KIND_LABELS = {
  symposium: {
    fr: { hero: "Symposiums", tagline: "Rencontres de pensée et de prière au Prieuré" },
    en: { hero: "Symposiums", tagline: "Encounters of thought and prayer at the Priory" },
  },
  stage: {
    fr: { hero: "Stages & Résidences", tagline: "Musique, chant, sculpture, peinture, cuisine, parfumerie" },
    en: { hero: "Workshops & Residencies", tagline: "Music, song, sculpture, painting, cuisine, perfumery" },
  },
};

const DISCIPLINE_LABELS = {
  musique: { fr: "Musique", en: "Music" },
  chant: { fr: "Chant", en: "Song" },
  sculpture: { fr: "Sculpture", en: "Sculpture" },
  peinture: { fr: "Peinture", en: "Painting" },
  cuisine: { fr: "Cuisine", en: "Cuisine" },
  parfumerie: { fr: "Parfumerie", en: "Perfumery" },
  autre: { fr: "Autre", en: "Other" },
};

export default function Programs({ kind = "symposium" }) {
  const { locale } = useI18n();
  const l = locale === "fr" ? "fr" : "en";
  const labels = KIND_LABELS[kind][l];
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get(`/programs?kind=${kind}`)
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, [kind]);

  const fmtDates = (a, b) => {
    if (!a) return "";
    try {
      const opts = { day: "numeric", month: "long", year: "numeric" };
      const dfmt = new Date(a).toLocaleDateString(l === "fr" ? "fr-FR" : "en-GB", opts);
      if (b && b !== a) {
        const end = new Date(b).toLocaleDateString(l === "fr" ? "fr-FR" : "en-GB", opts);
        return l === "fr" ? `Du ${dfmt} au ${end}` : `From ${dfmt} to ${end}`;
      }
      return dfmt;
    } catch {
      return a;
    }
  };

  return (
    <div data-testid={`programs-${kind}`} style={{ background: "var(--parchment)" }}>
      <section className="py-16 px-6 text-center border-b" style={{ borderColor: "var(--gold)" }}>
        <Ornament />
        <h1 className="font-heading text-4xl sm:text-5xl mt-4" style={{ color: "var(--ink)" }}>
          {labels.hero}
        </h1>
        <p className="font-body italic text-base mt-3 max-w-2xl mx-auto" style={{ color: "var(--muted)" }}>
          {labels.tagline}
        </p>
      </section>

      <section className="max-w-6xl mx-auto px-6 py-14">
        {loading ? (
          <p className="text-center italic" style={{ color: "var(--muted)" }}>…</p>
        ) : items.length === 0 ? (
          <p className="text-center italic" style={{ color: "var(--muted)" }}>
            {l === "fr" ? "Aucun programme publié pour le moment." : "No programme published yet."}
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {items.map((p) => (
              <Link
                key={p.id}
                to={`/${kind === "symposium" ? "symposiums" : "stages"}/${p.slug}`}
                data-testid={`program-card-${p.slug}`}
                className="block bg-white border p-6 transition-shadow hover:shadow-lg"
                style={{ borderColor: "var(--border)" }}
              >
                <div
                  className="font-caps text-xs mb-2"
                  style={{ color: "var(--gold)", letterSpacing: "0.22em" }}
                >
                  {fmtDates(p.start_date, p.end_date)}
                </div>
                <h2 className="font-heading text-2xl leading-tight mb-2" style={{ color: "var(--ink)" }}>
                  {pickLocalized(p.title, locale)}
                </h2>
                {p.subtitle && pickLocalized(p.subtitle, locale) && (
                  <p className="font-body italic text-sm mb-3" style={{ color: "var(--muted)" }}>
                    {pickLocalized(p.subtitle, locale)}
                  </p>
                )}
                {p.discipline && DISCIPLINE_LABELS[p.discipline] && (
                  <span
                    className="inline-block px-2 py-0.5 mb-3 font-caps text-xs"
                    style={{
                      background: "var(--parchment)",
                      border: "1px solid var(--gold)",
                      color: "var(--cinnabar)",
                      letterSpacing: "0.18em",
                    }}
                  >
                    {DISCIPLINE_LABELS[p.discipline][l]}
                  </span>
                )}
                <div className="mt-4 flex items-center gap-3 text-sm font-body" style={{ color: "var(--cinnabar)" }}>
                  <span>{l === "fr" ? "Découvrir" : "Discover"}</span>
                  <span style={{ color: "var(--gold)" }}>›</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
